import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import Button from "components/Button";
import { useArrangementDocument } from "hooks/useArrangementAPI";
import { useTimedStage } from "hooks/useTimedStage";

import { useArrangement } from "./ArrangementContext";
import PandaDocSigningSection from "./PandaDocSigningSection";
import ContinueOnBehalfPanel from "./ContinueOnBehalfPanel";
import CareSpecialistCard from "./CareSpecialistCard";
import ArrangementSpinner from "./ArrangementSpinner";
import ArrangementStepError from "./ArrangementStepError";
import { useLovedOneFirstName } from "./useLovedOneFirstName";
import { formatIsoDateTime, formatPersonName } from "./helpers";
import type { ArrangementDocumentState } from "./types";

// While the document is being created, creation_status sits in one of these.
const PREPARING_STATUSES = new Set(["not_started", "in_progress"]);
// The only document status a signing session can be created from.
const SIGNABLE_DOC_STATUS = "Awaiting signatures";
// A document is created as a draft and carries one of these statuses until it
// has been sent; it becomes signable shortly, so we keep waiting on it. Any
// other non-signable status is terminal and routes to the error screen.
const PREPARING_DOC_STATUSES = new Set(["Sending", "Draft"]);
// Sequenced status shown under the spinner while the document is generated.
// Each stage activates once its `afterMs` of elapsed time (from creation start)
// has passed; the latest wins, so the line advances through the steps and then
// holds on "Almost ready…" — kept vague so it never implies the document is
// ready before it actually is. `fallback` stages signal something likely went
// wrong and replace the heading rather than supplementing it. Add a row to
// introduce another stage.
const PREPARING_STAGES: readonly {
  afterMs: number;
  message: string;
  fallback?: boolean;
}[] = [
  { afterMs: 0, message: "Adding your selections…" },
  { afterMs: 2_500, message: "Formatting your agreement…" },
  { afterMs: 5_000, message: "Almost ready for your signature…" },
  {
    afterMs: 2 * 60 * 1000,
    message:
      "Your documents are taking longer than expected to prepare. Please refresh this page. If the problem persists, please contact your care specialist.",
    fallback: true,
  },
];
const PREPARING_POLL_MS = 1000;
// After the embed reports completion, poll faster while waiting for the
// PandaDoc webhook to mark the purchaser recipient complete.
const VERIFYING_POLL_MS = 500;
const VERIFY_TIMEOUT_MS = 30 * 1000;

/**
 * Sign-documents screen. Reads `GET /arrangement/document` and branches on
 * the async-creation status × whether the purchaser has signed × whether the
 * viewer is the purchaser. The PandaDoc embed itself lives in
 * PandaDocSigningSection — this component only decides when to show it.
 *
 * When creation is complete but the viewer is not the purchaser, it renders
 * the handoff panel + attestation flow instead of the embed.
 */
export default function SignDocumentsStep({
  onComplete,
  hasAttested,
  onAttested,
}: {
  onComplete: () => void;
  // Lifted to CompletionStep so one attestation covers signing + payment.
  hasAttested: boolean;
  onAttested: () => void;
}) {
  const { caseId } = useParams<{ caseId: string }>();
  const { isViewerPurchaser } = useArrangement();
  const firstName = useLovedOneFirstName();

  // Set when the embed reports completion; we then poll for the purchaser's
  // has_completed before showing the signed screen (the webhook is async).
  const [verifying, setVerifying] = useState(false);
  const [verifyTimedOut, setVerifyTimedOut] = useState(false);

  const [pollInterval, setPollInterval] = useState(PREPARING_POLL_MS);
  const { data } = useArrangementDocument(caseId, pollInterval);
  const doc = (data as ArrangementDocumentState | undefined) ?? null;
  const creationStatus = doc?.creation_status ?? "not_started";

  // The SOGS has exactly two recipients: the funeral home and the family
  // signer. The family signer's PandaDoc role is "informant" or "purchaser"
  // (it's "informant" when the informant is also the purchaser), so detect
  // the family signer as "the recipient that isn't the funeral home" rather
  // than matching a specific role. (A PandaDoc recipient role is unrelated to
  // a case-contact role.)
  const recipients = doc?.document?.recipients ?? [];
  const familyRecipient = recipients.find(
    (r) => (r.role ?? "").toLowerCase() !== "funeral-home",
  );
  const familySigned = familyRecipient?.has_completed === true;

  // creation_status "completed" means the document exists, not that it is
  // signable: it is created as a draft and must be sent first. A session can
  // only be created once the document reaches "Awaiting signatures".
  const docStatus = doc?.document?.status ?? null;
  const signable =
    creationStatus === "completed" && docStatus === SIGNABLE_DOC_STATUS;
  // Still on the way to signable: creation in flight, or sent-but-not-yet (or
  // its live status not yet known). Anything else is terminal (handled below).
  const preparing =
    PREPARING_STATUSES.has(creationStatus) ||
    (creationStatus === "completed" &&
      (docStatus === null || PREPARING_DOC_STATUSES.has(docStatus)));

  // Poll fast while verifying a fresh signature, slower while the document is
  // still being created or sent, and not at all once we have a terminal answer.
  useEffect(() => {
    if (familySigned) setPollInterval(0);
    else if (verifying) setPollInterval(VERIFYING_POLL_MS);
    else if (preparing) setPollInterval(PREPARING_POLL_MS);
    else setPollInterval(0);
  }, [verifying, familySigned, preparing]);

  // Hard timeout on signature verification — fall back to a refresh prompt.
  useEffect(() => {
    if (!verifying) {
      setVerifyTimedOut(false);
      return;
    }
    const id = setTimeout(() => setVerifyTimedOut(true), VERIFY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [verifying]);

  // Staged contextual copy shown while the document is being prepared.
  const prepStage = useTimedStage({
    active: preparing,
    stages: PREPARING_STAGES,
    startAt: doc?.creation_started_at
      ? new Date(doc.creation_started_at).getTime()
      : undefined,
  });

  // First /document read in flight.
  if (!doc) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <ArrangementSpinner />
      </div>
    );
  }

  // Family signer has signed — confirmation + continue (shown to every viewer).
  if (familySigned) {
    const name = formatPersonName(familyRecipient);
    const when = formatIsoDateTime(familyRecipient?.completed_at);
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-sage" />
        <h2 className="mt-4 font-serif text-2xl italic text-evergreen">
          Documents signed
        </h2>
        <p className="mt-3 text-soil">
          {name ? `Signed by ${name}` : "Your documents are signed"}
          {when ? ` at ${when}` : ""}.
        </p>
        <div className="mt-8 flex justify-center">
          <Button variant="primary" onClick={onComplete}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Creation failed — no retry button per spec; direct to care.
  if (creationStatus === "failed") {
    return (
      <ArrangementStepError message="Something went wrong preparing your documents. Please try again later or contact your care specialist.">
        <CareSpecialistCard />
      </ArrangementStepError>
    );
  }

  // Still being prepared (creation running, or created but not yet sent).
  if (preparing) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <ArrangementSpinner />
        {prepStage?.fallback ? (
          <p className="mx-auto mt-6 max-w-md text-lg text-soil">
            {prepStage.message}
          </p>
        ) : (
          <>
            <h2 className="mt-6 font-serif text-2xl italic text-evergreen">
              Preparing your documents
            </h2>
            {prepStage && (
              <p className="mt-2 text-soil transition-opacity">
                {prepStage.message}
              </p>
            )}
            <p className="mx-auto mt-6 max-w-md text-soil/80">
              Your Statement of Funeral Goods and Services will appear here to
              sign — nothing to print or mail. Once it&apos;s signed, your care
              specialist can begin working on {firstName}&apos;s arrangements.
            </p>
          </>
        )}
      </div>
    );
  }

  // Signature submitted in the embed — wait for the webhook to mark the
  // family signer complete (the familySigned branch above takes over when it
  // does). Falls back to a refresh prompt after the hard timeout.
  if (verifying) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <ArrangementSpinner />
        <p className="mx-auto mt-6 max-w-md text-lg text-soil">
          {verifyTimedOut
            ? "Your signature was recorded. Refresh to continue."
            : "Verifying signature…"}
        </p>
      </div>
    );
  }

  // Document is sent and awaiting signatures, and the family signer hasn't
  // signed yet. The purchaser (or a non-purchaser who has attested) signs via
  // the embed; everyone else gets the handoff panel first.
  if (signable) {
    if (isViewerPurchaser || hasAttested) {
      return <PandaDocSigningSection onCompleted={() => setVerifying(true)} />;
    }
    return <ContinueOnBehalfPanel onAttested={onAttested} />;
  }

  // Document reached a non-signable terminal state (voided, declined, errored,
  // outdated, …). There's nothing to sign — direct to care.
  return (
    <ArrangementStepError message="This document is no longer available for signing. Please contact your care specialist for assistance.">
      <CareSpecialistCard />
    </ArrangementStepError>
  );
}
